import React, { useState, useEffect } from 'react';
import { View, Text, ScrollView, StyleSheet } from 'react-native';
import LoadingState from '../components/common/LoadingState';
import ErrorState from '../components/common/ErrorState';

const DynamicPrivacyPolicy = () => {
  const [policyContent, setPolicyContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState('');

  useEffect(() => {
    fetchPrivacyPolicy();
  }, []);

  const fetchPrivacyPolicy = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('http://localhost:5001/api/privacy-policy');
      const data = await response.json();
      
      if (data.success) {
        setPolicyContent(data.content);
        setLastUpdated(data.lastUpdated);
      } else {
        throw new Error('Failed to load privacy policy');
      }
    } catch (err) {
      console.error('Error fetching privacy policy:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Simple markdown to React conversion (basic)
  const renderMarkdownContent = (markdown) => {
    const lines = markdown.split('\n');
    const elements = [];
    let currentKey = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      if (line.startsWith('# ')) {
        elements.push(
          <Text key={currentKey++} style={styles.title}>
            {line.replace('# ', '')}
          </Text>
        );
      } else if (line.startsWith('## ')) {
        elements.push(
          <Text key={currentKey++} style={styles.sectionTitle}>
            {line.replace('## ', '')}
          </Text>
        );
      } else if (line.startsWith('### ')) {
        elements.push(
          <Text key={currentKey++} style={styles.subsectionTitle}>
            {line.replace('### ', '')}
          </Text>
        );
      } else if (line.startsWith('- ')) {
        elements.push(
          <Text key={currentKey++} style={styles.bullet}>
            • {line.replace('- ', '')}
          </Text>
        );
      } else if (line.trim() && !line.startsWith('**')) {
        elements.push(
          <Text key={currentKey++} style={styles.paragraph}>
            {line}
          </Text>
        );
      } else if (line.startsWith('**') && line.endsWith('**')) {
        elements.push(
          <Text key={currentKey++} style={styles.bold}>
            {line.replace(/\*\*/g, '')}
          </Text>
        );
      }
    }

    return elements;
  };

  if (loading) return <LoadingState message="Loading privacy policy..." />;
  if (error) return <ErrorState message={error} onRetry={fetchPrivacyPolicy} />;

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.header}>
        <Text style={styles.dynamicBadge}>🔄 Dynamic Content</Text>
        <Text style={styles.lastUpdated}>
          API Last Updated: {new Date(lastUpdated).toLocaleString()}
        </Text>
      </View>

      <View style={styles.section}>
        {renderMarkdownContent(policyContent)}
      </View>

      <View style={styles.footer}>
        <Text style={styles.footerText}>
          This content is loaded dynamically from the backend markdown file.
          Changes to ZECHER_PRIVACY_POLICY.md will appear immediately here.
        </Text>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  content: {
    maxWidth: 900,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: 24,
    paddingVertical: 32,
    paddingBottom: 64,
  },
  header: {
    marginBottom: 32,
    alignItems: 'center',
  },
  dynamicBadge: {
    fontSize: 16,
    color: '#059669',
    backgroundColor: '#F0FDF4',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 16,
    marginBottom: 8,
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 16,
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 16,
    marginTop: 24,
  },
  subsectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 12,
    marginTop: 16,
  },
  paragraph: {
    fontSize: 16,
    lineHeight: 24,
    color: '#374151',
    marginBottom: 12,
  },
  bullet: {
    fontSize: 16,
    lineHeight: 24,
    color: '#374151',
    marginBottom: 8,
    marginLeft: 16,
  },
  bold: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 12,
  },
  lastUpdated: {
    fontSize: 14,
    color: '#6B7280',
  },
  section: {
    marginBottom: 32,
  },
  footer: {
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 24,
    marginTop: 32,
  },
  footerText: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    fontStyle: 'italic',
    lineHeight: 20,
  },
});

export default DynamicPrivacyPolicy; 